'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { MediaAsset } from 'libs/contentful/types';
import Media from 'components/shared/Media/Media';

// Enough logos per half of the track to span the widest desktop screen.
const MIN_LOGOS_PER_HALF = 10;
// 3.4s per logo keeps a 10-logo marquee at 34s while longer tracks hold the same speed.
const SECONDS_PER_LOGO = 3.4;

interface LogoMarqueeProps {
  logos: MediaAsset[];
  headline?: ReactNode;
  sectionName?: string;
}

export function LogoMarquee({ logos, headline, sectionName }: LogoMarqueeProps) {
  const headingId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasLogos = logos.length > 0;
  // The track slides by half its width, so an even number of copies keeps the loop seamless.
  const halfCopies = hasLogos ? Math.ceil(MIN_LOGOS_PER_HALF / logos.length) : 1;
  const copies = Array.from({ length: halfCopies * 2 }, (_, copy) => copy);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting));
    observer.observe(section);
    return () => observer.disconnect();
  }, [hasLogos]);

  if (!hasLogos) return null;

  return (
    <section
      id={sectionName}
      ref={sectionRef}
      aria-label={headline ? undefined : 'Logos'}
      aria-labelledby={headline ? headingId : undefined}
      className="dark-green-colorway relative flex min-h-60 flex-col justify-center overflow-hidden bg-[linear-gradient(180deg,var(--color-soft-black)_0%,var(--color-kale)_100%)] pb-[60px]">
      {headline && <div id={headingId}>{headline}</div>}
      <div className="overflow-hidden motion-reduce:overflow-x-auto">
        <div
          className={`flex w-max animate-[marquee_34s_linear_infinite] motion-reduce:animate-none ${isVisible ? '' : 'animate-pause'}`}
          style={{ animationDuration: `${SECONDS_PER_LOGO * logos.length * halfCopies}s` }}>
          {copies.map(copy => (
            <div key={copy} className="flex flex-none" aria-hidden={copy > 0}>
              {logos.map((logo, index) => {
                // Fit each logo inside 280x96 without upscaling it.
                const width = logo.width ?? 280;
                const height = logo.height ?? 96;
                const scale = Math.min(1, 280 / width, 96 / height);
                return (
                  <div key={`${logo.url}-${index}`} className="flex shrink-0 items-center pr-[102px]">
                    <div style={{ width: width * scale, height: height * scale }}>
                      <Media media={logo} className="h-full w-full object-contain" sizes="280px" roundedImage={false} />
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
